import React from 'react'
import ReactDOM from 'react-dom/client'
import { WindowContextProvider } from './components/layout/Titlebar'
import { ModeProvider } from './contexts/ModeProvider'
import { LanguageProvider } from './contexts/LanguageProvider'
import { DisplayModeProvider } from './contexts/DisplayModeProvider'
import { WindowModeProvider } from './contexts/WindowModeProvider'
import App from './App'
import { ErrorBoundary } from './components/common/ErrorBoundary'
import { analyticsWaitsForShow, initAnalytics } from './lib/analytics'
import { initSentry } from './lib/sentry'
import { bootstrapProfileSyncBridge } from './lib/profile'
import { AppLogoIcon } from './components/icons/AppLogoIcon'
import './styles/tailwind.css' // MUST be first — Tailwind preflight + utilities, depended on by shadcn primitives
import './styles/app.scss'
import './styles/globals.scss'
import './i18n/config'
import { TooltipProvider } from './components/ui/tooltip'

// Note: Supabase auth token handling is done in index.html (inline script)
// This ensures the hash is cleaned BEFORE React Router initializes

// Platform CSS class for native cursor behavior (Electron vs Web)
const isElectronPlatform = typeof window !== 'undefined' && 'api' in window && window.api !== undefined
document.documentElement.classList.add(isElectronPlatform ? 'platform-electron' : 'platform-web')

// Initialize PostHog analytics (no-op if env vars not set). Die vorgewärmte Schnelleingabe startet
// es erst beim ersten Zeigen (`analyticsWaitsForShow`).
try {
  if (!analyticsWaitsForShow(window.location.hash)) initAnalytics()
} catch (e) {
  console.error('[Analytics] Failed to initialize:', e)
}

// Initialize Sentry for error tracking and user feedback (no-op if DSN not set)
initSentry()

// Bridge cross-window profile-update broadcasts into the in-process event
// bus so consumers (sidebar avatar, user-menu label) listen to a single
// source. See `lib/profile/bootstrapProfileSyncBridge` and the main-process
// `user-profile-updated` IPC handler.
bootstrapProfileSyncBridge()


// Signals main process after React mount + paint so the window shows without flash.
function ReadySignal() {
  React.useEffect(() => {
    // useEffect = React has committed to DOM.
    // Double rAF = browser has painted at least one styled frame.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        window.api?.send?.('renderer-ready')
      })
    })
  }, [])
  return null
}

ReactDOM.createRoot(document.getElementById('app') as HTMLElement).render(
  <React.StrictMode>
    <ModeProvider>
      <LanguageProvider>
        <DisplayModeProvider>
          <WindowContextProvider
            titlebar={{
              icon: <AppLogoIcon size={24} />,
            }}
          >
            <WindowModeProvider>
              {/* Ein Provider fürs ganze Fenster: Von Tooltip zu Tooltip erscheint der nächste sofort. */}
              <TooltipProvider>
                <ErrorBoundary variant="page" name="App">
                  <App />
                </ErrorBoundary>
              </TooltipProvider>
              <ReadySignal />
            </WindowModeProvider>
          </WindowContextProvider>
        </DisplayModeProvider>
      </LanguageProvider>
    </ModeProvider>
  </React.StrictMode>
)
